import React from 'react';
import {Box, InputBase, ToggleButton, ToggleButtonGroup, Typography} from '@mui/material';
import ArrowUpwardIcon from '@mui/icons-material/ArrowUpward';
import ArrowDownwardIcon from '@mui/icons-material/ArrowDownward';
import VerticalAlignTopIcon from '@mui/icons-material/VerticalAlignTop';
import VerticalAlignBottomIcon from '@mui/icons-material/VerticalAlignBottom';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import FormatAlignLeftIcon from '@mui/icons-material/FormatAlignLeft';
import FormatAlignCenterIcon from '@mui/icons-material/FormatAlignCenter';
import FormatAlignRightIcon from '@mui/icons-material/FormatAlignRight';
import {useTranslation} from 'react-i18next';
import {Scene} from '../../../scene/types';
import {canMoveLayer, DEFAULT_TEXT_ALIGN, LAST_RESORT_TEXT_COLOR, UserLayer, UserLayerStyle} from '../../../scene/userLayers';

/** Shared control chrome for a user layer, used by BOTH the desktop layer card
 *  and the Fill Mode sheets — the two surfaces must offer exactly the same set
 *  of layer operations, and duplicating them is how they drift. */

/** Icon + short label, stacked. The icons alone were unreadable — an arrow with
 *  no word does not say whether it moves the layer or the selection — so every
 *  z-order action now carries its verb. Disabled state is a 0.55 opacity on the
 *  WHOLE button rather than MUI's default grey, which on this dark surface was
 *  nearly invisible against the enabled state. */
const zButtonSx = (dense?: boolean) => ({
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    gap: '2px',
    flex: 1,
    minWidth: 0,
    padding: dense ? '6px 2px' : '5px 2px',
    background: 'transparent',
    color: '#C3F3FD',
    border: '1px solid #ffffff40',
    borderRadius: '10px',
    cursor: 'pointer',
    lineHeight: 1,
    '&:disabled': {opacity: 0.55, cursor: 'default'},
} as const);

export interface LayerZOrderControlsProps {
    layer: UserLayer;
    layers: UserLayer[];
    /** validSlots(scene) — the legal `order` values (never inside an
     *  image+__overlay unit). */
    slots: number[];
    onMove: (id: string, move: 'up' | 'down' | 'front' | 'back') => void;
    onDelete: (id: string) => void;
    /** Larger, finger-sized buttons for the phone sheets. */
    dense?: boolean;
}

/** Forward / backward / to-front / to-back + delete. Disabled at the ends, so
 *  the buttons say where the layer already is instead of no-op'ing silently. */
export const LayerZOrderControls: React.FC<LayerZOrderControlsProps> = ({
                                                                           layer,
                                                                           layers,
                                                                           slots,
                                                                           onMove,
                                                                           onDelete,
                                                                           dense,
                                                                       }) => {
    const {t} = useTranslation();
    const canUp = canMoveLayer(layers, layer.id, 'up', slots);
    const canDown = canMoveLayer(layers, layer.id, 'down', slots);

    const button = (
        key: string,
        labelKey: string,
        icon: React.ReactNode,
        disabled: boolean,
        onClick: () => void,
    ) => (
        <Box
            component="button"
            type="button"
            key={key}
            sx={zButtonSx(dense)}
            disabled={disabled}
            aria-label={t(labelKey)}
            title={t(labelKey)}
            data-testid={`layer-${key}-${layer.id}`}
            // Keeps the iOS keyboard up when these sit next to a focused
            // textarea (same reason as the font steppers).
            onPointerDown={(e: React.PointerEvent) => e.preventDefault()}
            onClick={onClick}
        >
            {icon}
            <Typography component="span" sx={{fontSize: dense ? '0.62rem' : '0.6rem', fontWeight: 600, lineHeight: 1.1}}>
                {t(labelKey)}
            </Typography>
        </Box>
    );

    return (
        // One row on the phone too: four verbs plus delete, each shrinking
        // rather than wrapping.
        <Box sx={{display: 'flex', gap: 0.5, width: '100%'}}>
            {button('forward', 'editor.layers.forwardShort', <ArrowUpwardIcon fontSize="small"/>, !canUp, () => onMove(layer.id, 'up'))}
            {button('backward', 'editor.layers.backwardShort', <ArrowDownwardIcon fontSize="small"/>, !canDown, () => onMove(layer.id, 'down'))}
            {button('front', 'editor.layers.toFrontShort', <VerticalAlignTopIcon fontSize="small"/>, !canUp, () => onMove(layer.id, 'front'))}
            {button('back', 'editor.layers.toBackShort', <VerticalAlignBottomIcon fontSize="small"/>, !canDown, () => onMove(layer.id, 'back'))}
            {button('delete', 'editor.layers.delete', <DeleteOutlineIcon fontSize="small"/>, false, () => onDelete(layer.id))}
        </Box>
    );
};

/** The palette offered as swatches. Free hex input covers everything else, so
 *  this is a shortcut list, not a constraint. */
export const LAYER_COLOR_SWATCHES: { hex: string; labelKey: string }[] = [
    {hex: '#FFFFFF', labelKey: 'editor.layers.colorWhite'},
    {hex: '#000000', labelKey: 'editor.layers.colorBlack'},
    {hex: '#00E162', labelKey: 'editor.layers.colorGreen'},
    {hex: '#2ea6e6', labelKey: 'editor.layers.colorBlue'},
    {hex: '#F5C518', labelKey: 'editor.layers.colorYellow'},
    {hex: '#E4572E', labelKey: 'editor.layers.colorOrange'},
];

const HEX = /^#([0-9a-fA-F]{3}|[0-9a-fA-F]{6})$/;

export interface LayerTextStyleControlsProps {
    layer: UserLayer;
    /** The MERGED scene. A layer with no stored colour INHERITS the design's
     *  reference text colour, resolved by the merge — so the swatch/hex must
     *  read what is actually on the board, not a default this component made up. */
    scene?: Scene;
    onStyle: (id: string, style: Partial<UserLayerStyle>) => void;
    dense?: boolean;
}

/** The colour the board is actually drawing for this layer. */
export const resolvedLayerColor = (layer: UserLayer, scene?: Scene): string => {
    if (layer.style?.color) return layer.style.color;
    const el = scene?.elements?.find((e) => e.id === layer.id);
    const fill = el?.text?.fill;
    if (fill && fill.type === 'solid' && fill.color) return fill.color;
    return LAST_RESORT_TEXT_COLOR;
};

/** Colour swatches (+ free hex) and physical left/center/right alignment — the
 *  same three values the renderer understands (SceneTextSpec.align). */
export const LayerTextStyleControls: React.FC<LayerTextStyleControlsProps> = ({layer, scene, onStyle, dense}) => {
    const {t} = useTranslation();
    const color = resolvedLayerColor(layer, scene);
    const align = layer.style?.align ?? DEFAULT_TEXT_ALIGN;
    const [hex, setHex] = React.useState(color);
    // Follow an external change (undo, design switch) without fighting typing.
    React.useEffect(() => setHex(color), [color]);

    const commitHex = (value: string) => {
        const trimmed = value.trim();
        const normalized = trimmed.startsWith('#') ? trimmed : `#${trimmed}`;
        if (HEX.test(normalized)) onStyle(layer.id, {color: normalized.toUpperCase()});
    };

    const size = dense ? 32 : 26;

    return (
        <Box sx={{display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap'}}>
            <Box sx={{display: 'flex', gap: 0.5}} role="group" aria-label={t('editor.layers.color')}>
                {LAYER_COLOR_SWATCHES.map(({hex, labelKey}) => {
                    const selected = color.toUpperCase() === hex.toUpperCase();
                    return (
                        <Box
                            component="button"
                            type="button"
                            key={hex}
                            aria-label={t(labelKey)}
                            aria-pressed={selected}
                            title={t(labelKey)}
                            data-testid={`layer-color-${hex}-${layer.id}`}
                            onPointerDown={(e: React.PointerEvent) => e.preventDefault()}
                            onClick={() => onStyle(layer.id, {color: hex})}
                            sx={{
                                width: size,
                                height: size,
                                borderRadius: '50%',
                                cursor: 'pointer',
                                backgroundColor: hex,
                                border: selected ? '2px solid #00E162' : '1px solid #ffffff60',
                                padding: 0,
                            }}
                        />
                    );
                })}
            </Box>
            <InputBase
                value={hex}
                onChange={(e) => setHex(e.target.value)}
                onBlur={(e) => commitHex(e.target.value)}
                onKeyDown={(e) => {
                    if (e.key === 'Enter') commitHex((e.target as HTMLInputElement).value);
                }}
                inputProps={{
                    'aria-label': t('editor.layers.color'),
                    maxLength: 7,
                    spellCheck: false,
                    dir: 'ltr',
                    'data-testid': `layer-color-hex-${layer.id}`,
                }}
                sx={{
                    width: 82,
                    '& .MuiInputBase-input': {
                        backgroundColor: '#071826',
                        border: '1px solid #2ea6e6',
                        borderRadius: '8px',
                        color: '#fff',
                        fontSize: '0.8rem',
                        padding: '4px 8px',
                        textAlign: 'center',
                    },
                }}
            />
            <ToggleButtonGroup
                size="small"
                exclusive
                value={align}
                onChange={(_, value) => value && onStyle(layer.id, {align: value})}
                sx={{
                    '& .MuiToggleButton-root': {
                        color: '#C3F3FD',
                        borderColor: '#ffffff40',
                        padding: dense ? '8px' : '4px',
                    },
                    '& .Mui-selected': {backgroundColor: '#2ea6e644'},
                }}
            >
                <ToggleButton
                    value="left"
                    aria-label={t('editor.layers.alignLeft')}
                    data-testid={`layer-align-left-${layer.id}`}
                    onPointerDown={(e) => e.preventDefault()}
                >
                    <FormatAlignLeftIcon fontSize="small"/>
                </ToggleButton>
                <ToggleButton
                    value="center"
                    aria-label={t('editor.layers.alignCenter')}
                    data-testid={`layer-align-center-${layer.id}`}
                    onPointerDown={(e) => e.preventDefault()}
                >
                    <FormatAlignCenterIcon fontSize="small"/>
                </ToggleButton>
                <ToggleButton
                    value="right"
                    aria-label={t('editor.layers.alignRight')}
                    data-testid={`layer-align-right-${layer.id}`}
                    onPointerDown={(e) => e.preventDefault()}
                >
                    <FormatAlignRightIcon fontSize="small"/>
                </ToggleButton>
            </ToggleButtonGroup>
        </Box>
    );
};
